import React, {useEffect, useMemo, useRef, useState} from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  SelectDateInputWrapper,
  CalendarHeadersRow,
  CalendarHeaderCell,
  CalendarNavButton,
  TodayBar,
  BottomActionBar,
  TodayBarSides,
  TodayBarRightMuted,
} from './SelectDateInput.styles';
import {ArrowLeft, ArrowRight, ChevronLeft, ChevronRight} from '@carbon/icons-react';
import {theme} from '@styles/theme';
import Button from '@components/atoms/Button';
import OptionButton from '@atoms/OptionButton';
import Text from '@atoms/Text';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '@store';
import {fetchCalendarRange, selectCalendarSnapshotIndex} from '@store/slices/calendar';
import {selectCoveragePlans} from '@store/slices/coveragePlans';
import Tooltip from '@components/atoms/Tooltip';
import {
  formatDateSmart,
  formatMonth,
  formatNonTodayFull,
  formatTodayFull,
  addMonths,
  toISODateString,
  formatDateDMY,
} from '@utils/common';
import {PlanActionsDropdownWrapper} from '@components/atoms/OptionButton.styles';

type CalendarView = 'month' | 'year' | 'decade' | 'century';
type CalendarValue = Date | [Date | null, Date | null] | null;

type TileDisabledArgs = {date: Date; view: CalendarView};

export type SelectDateInputProps = {
  onRangeChange?: (range: [Date, Date]) => void;
  onCompare?: (
    left: Date,
    right: Date,
    mode?: 'data-data' | 'plan-plan' | 'data-plan' | 'plan-data',
  ) => void;
  onCloseDropdown?: () => void;
  onShowSingle?: (date: Date) => void;
  onShowPlan?: (date: Date) => void;
};

export default function DoubleRangeCalendar({
  onRangeChange,
  onCompare,
  onCloseDropdown,
  onShowSingle,
  onShowPlan,
}: SelectDateInputProps): React.ReactElement {
  const today = useMemo<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [range, setRange] = useState<[Date, Date] | null>(null);
  const lastHoveredDisabledTileRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipTop, setTooltipTop] = useState<number>(0);
  const [tooltipLeft, setTooltipLeft] = useState<number>(0);
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);
  const [tooltipText, setTooltipText] = useState<string>('');

  const plansForSystem = useSelector(selectCoveragePlans);
  const plannedFromStore = useMemo(
    () => plansForSystem.map(p => p.date),
    [plansForSystem],
  );
  const plannedDates = plannedFromStore;

  const snapshotIndex = useSelector(selectCalendarSnapshotIndex);
  const [earliestDataISO, setEarliestDataISO] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshotIndex || snapshotIndex.length === 0) return;
    const dataDates = snapshotIndex
      .filter(s => !plannedDates.includes(s.dateISO))
      .map(s => s.dateISO)
      .sort();
    const minInBatch = dataDates[0] ?? null;
    if (minInBatch) {
      setEarliestDataISO(prev => (prev == null || minInBatch < prev ? minInBatch : prev));
    }
  }, [snapshotIndex, plannedDates]);

  const initialLeftMonth = useMemo<Date>(() => addMonths(today, -1), [today]);
  const [activeStartDate, setActiveStartDate] = useState<Date>(initialLeftMonth);
  const dispatch = useDispatch<AppDispatch>();

  const emit = (r: [Date, Date]): void => {
    onRangeChange?.(r);
  };

  const onChange = (nextValue: CalendarValue): void => {
    if (nextValue instanceof Date) {
      const d = new Date(nextValue);
      d.setHours(0, 0, 0, 0);
      setRange([d, d]);
      emit([d, d]);
      return;
    }
    if (Array.isArray(nextValue) && nextValue.length === 2) {
      const [a, b] = nextValue;
      if (a instanceof Date && b instanceof Date) {
        const aN = new Date(a);
        aN.setHours(0, 0, 0, 0);
        const bN = new Date(b);
        bN.setHours(0, 0, 0, 0);
        setRange([aN, bN]);
        emit([aN, bN]);
      } else if (a instanceof Date && (b === null || b === undefined)) {
        const aN = new Date(a);
        aN.setHours(0, 0, 0, 0);
        setRange([aN, aN]);
        emit([aN, aN]);
      }
    }
  };

  const tileDisabled = ({date, view}: TileDisabledArgs): boolean => {
    if (view !== 'month') return false;
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    const isFuture = day.getTime() > today.getTime();
    const iso = toISODateString(day);
    if (isFuture) {
      return !plannedDates.includes(iso);
    }
    const isBeforeEarliestData = earliestDataISO ? iso < earliestDataISO : false;
    if (isBeforeEarliestData) return true;
    return false;
  };

  const tileClassName = ({date, view}: {date: Date; view: CalendarView}) => {
    if (view !== 'month') return undefined;
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    const iso = toISODateString(day);
    const isFuture = day.getTime() > today.getTime();
    if (plannedDates.includes(iso)) {
      return 'planned-future';
    }
    if (!isFuture && earliestDataISO && iso < earliestDataISO) return 'disabled-past';
    return undefined;
  };

  const calendarValue: CalendarValue = range ? [range[0], range[1]] : null;

  const isSingleSelection = !!range && range[0].getTime() === range[1].getTime();
  const isSingleToday = isSingleSelection && range![0].getTime() === today.getTime();
  const isSingleNonToday = isSingleSelection && !isSingleToday;
  const isDoubleSelection = !!range && range[0].getTime() !== range[1].getTime();
  const isSinglePlanned =
    isSingleNonToday && !!range && plannedDates.includes(toISODateString(range[0]));

  const leftDate: Date | null = useMemo(() => {
    if (!isDoubleSelection || !range) return null;
    return range[0] < range[1] ? range[0] : range[1];
  }, [isDoubleSelection, range]);
  const rightDate: Date | null = useMemo(() => {
    if (!isDoubleSelection || !range) return null;
    return range[0] < range[1] ? range[1] : range[0];
  }, [isDoubleSelection, range]);
  const bothPlanned: boolean = useMemo(() => {
    if (!leftDate || !rightDate) return false;
    const leftISO = toISODateString(leftDate);
    const rightISO = toISODateString(rightDate);
    return plannedDates.includes(leftISO) && plannedDates.includes(rightISO);
  }, [leftDate, rightDate, plannedDates]);

  const leftRightBar =
    isDoubleSelection && range ? (
      <TodayBarSides>
        <span>{formatDateSmart(range[0] < range[1] ? range[0] : range[1], today)}</span>
        <span>
          <ChevronRight size={16} fill={theme.colors.basic02} />
          <ChevronLeft size={16} fill={theme.colors.basic02} />
        </span>
        {(() => {
          const rightDate = range[0] > range[1] ? range[0] : range[1];
          const isFuture = rightDate.getTime() > today.getTime();
          const iso = toISODateString(rightDate);
          const isPlanned = plannedDates.includes(iso);
          return (
            <span>
              {isFuture && isPlanned
                ? `План на ${formatNonTodayFull(rightDate)}`
                : formatDateSmart(rightDate, today)}
            </span>
          );
        })()}
      </TodayBarSides>
    ) : isSingleNonToday && range ? (
      <TodayBarSides>
        <span>{formatNonTodayFull(range[0])}</span>
        <span></span>
        <TodayBarRightMuted>{formatTodayFull(today)}</TodayBarRightMuted>
      </TodayBarSides>
    ) : (
      formatTodayFull(today)
    );

  useEffect(() => {
    const handleWindowMouseMove = (e: MouseEvent): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) {
        lastHoveredDisabledTileRef.current = null;
        setTooltipVisible(false);
        return;
      }
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!el) {
        lastHoveredDisabledTileRef.current = null;
        return;
      }
      const plannedTile = el.closest(
        '.react-calendar__tile.planned-future',
      ) as HTMLElement | null;
      const disabledTile = el.closest(
        '.react-calendar__tile--disabled, .react-calendar__tile:disabled, button.react-calendar__tile:disabled',
      ) as HTMLElement | null;

      if (plannedTile && plannedTile !== lastHoveredDisabledTileRef.current) {
        lastHoveredDisabledTileRef.current = plannedTile;
        const tileRect = plannedTile.getBoundingClientRect();
        const topInViewport = tileRect.bottom;
        const leftInViewport = tileRect.left;
        setTooltipTop(topInViewport);
        setTooltipLeft(leftInViewport);
        setTooltipWidth(tileRect.width);
        setTooltipText('Выберите, чтобы просмотреть план покрытия');
        setTooltipVisible(true);
      } else if (disabledTile && disabledTile !== lastHoveredDisabledTileRef.current) {
        lastHoveredDisabledTileRef.current = disabledTile;
        const tileRect = disabledTile.getBoundingClientRect();
        const topInViewport = tileRect.bottom;
        const leftInViewport = tileRect.left;
        setTooltipTop(topInViewport);
        setTooltipLeft(leftInViewport);
        setTooltipWidth(tileRect.width);
        setTooltipText('Нет данных');
        setTooltipVisible(true);
      } else if (!plannedTile && !disabledTile) {
        lastHoveredDisabledTileRef.current = null;
        setTooltipVisible(false);
      }
    };
    window.addEventListener('mousemove', handleWindowMouseMove, {passive: true});
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove as EventListener);
    };
  }, []);

  useEffect(() => {
    const leftMonth = new Date(activeStartDate);
    leftMonth.setHours(0, 0, 0, 0);
    const rightMonth = addMonths(activeStartDate, 1);
    rightMonth.setHours(0, 0, 0, 0);
    const start = new Date(leftMonth.getFullYear(), leftMonth.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(rightMonth.getFullYear(), rightMonth.getMonth() + 1, 0);
    end.setHours(0, 0, 0, 0);
    const before_date = toISODateString(start);
    const after_date = toISODateString(end);
    dispatch(fetchCalendarRange({before_date, after_date}));
  }, [activeStartDate, dispatch]);

  useEffect(() => {
    const before_date = '2024-01-01';
    const after_date = toISODateString(today);
    dispatch(fetchCalendarRange({before_date, after_date}));
  }, []);

  return (
    <SelectDateInputWrapper ref={wrapperRef}>
      <TodayBar>{leftRightBar}</TodayBar>
      <CalendarHeadersRow>
        <CalendarNavButton
          onClick={() => setActiveStartDate(addMonths(activeStartDate, -1))}>
          <ArrowLeft width={16} fill={theme.colors.accent00} />
        </CalendarNavButton>

        <CalendarHeaderCell>{formatMonth(activeStartDate)}</CalendarHeaderCell>
        <CalendarHeaderCell>
          {formatMonth(addMonths(activeStartDate, 1))}
        </CalendarHeaderCell>
        <CalendarNavButton
          onClick={() => setActiveStartDate(addMonths(activeStartDate, 1))}>
          <ArrowRight size={16} fill={theme.colors.accent00} />
        </CalendarNavButton>
      </CalendarHeadersRow>
      <Calendar
        selectRange
        allowPartialRange
        onChange={onChange}
        value={calendarValue}
        showDoubleView
        showFixedNumberOfWeeks={false}
        showNeighboringMonth={false}
        minDetail="month"
        locale="ru-RU"
        tileDisabled={tileDisabled}
        tileClassName={tileClassName}
        prev2Label={null}
        next2Label={null}
        activeStartDate={activeStartDate}
        onActiveStartDateChange={({activeStartDate}) => {
          if (activeStartDate) {
            setActiveStartDate(activeStartDate);
          }
        }}
      />
      {(isDoubleSelection || isSingleSelection) && (
        <BottomActionBar>
          {isDoubleSelection && range ? (
            bothPlanned && onCompare ? (
              <>
                <Text text="Выберите действие →" type="bodyM" />
                <OptionButton
                  value={'compare'}
                  dropdownContent={
                    <PlanActionsDropdownWrapper>
                      <Button
                        title="Сравнить данные"
                        variant="text"
                        onClick={() => {
                          if (leftDate && rightDate)
                            onCompare?.(leftDate, rightDate, 'data-data');
                          onCloseDropdown?.();
                        }}
                      />
                      <Button
                        title="Сравнить планы"
                        variant="text"
                        onClick={() => {
                          if (leftDate && rightDate)
                            onCompare?.(leftDate, rightDate, 'plan-plan');
                          onCloseDropdown?.();
                        }}
                      />
                      <Button
                        title={`Сравнить данные ${formatDateDMY(
                          leftDate!,
                        )} с планом ${formatDateDMY(rightDate!)}`}
                        variant="text"
                        onClick={() => {
                          if (leftDate && rightDate)
                            onCompare?.(leftDate, rightDate, 'data-plan');
                          onCloseDropdown?.();
                        }}
                      />
                      <Button
                        title={`Сравнить план ${formatDateDMY(
                          leftDate!,
                        )} с данными ${formatDateDMY(rightDate!)}`}
                        variant="text"
                        onClick={() => {
                          if (leftDate && rightDate)
                            onCompare?.(leftDate, rightDate, 'plan-data');
                          onCloseDropdown?.();
                        }}
                      />
                    </PlanActionsDropdownWrapper>
                  }
                  listPositionOverride="bottom"
                  dropdownAlign="right"
                  variant="filledBlue"
                  withoutSeparator
                  withoutIcon
                  buttonTitle="Выберите действие"
                />
              </>
            ) : (
              <Button
                variant="filled"
                size="regular"
                title={'Сравнить выбранные даты'}
                onClick={() => {
                  const l = leftDate!;
                  const r = rightDate!;
                  const isLeftPlanned = plannedDates.includes(toISODateString(l));
                  const isRightPlanned = plannedDates.includes(toISODateString(r));
                  const mode =
                    isLeftPlanned && !isRightPlanned
                      ? 'plan-data'
                      : !isLeftPlanned && isRightPlanned
                        ? 'data-plan'
                        : 'data-data';
                  onCompare?.(l, r, mode);
                  onCloseDropdown?.();
                }}
              />
            )
          ) : isSinglePlanned && range ? (
            <>
              <Text text="Выберите действие →" type="bodyM" />
              <OptionButton
                value={'show'}
                dropdownContent={
                  <PlanActionsDropdownWrapper>
                    <Button
                      title="Показать план"
                      variant="text"
                      onClick={() => {
                        if (range) {
                          onShowPlan?.(range[0]);
                        }
                        onCloseDropdown?.();
                      }}
                    />
                    <Button
                      title="Сравнить с планом"
                      variant="text"
                      onClick={() => {
                        if (range) {
                          onCompare?.(range[0], range[0], 'data-plan');
                        }
                        onCloseDropdown?.();
                      }}
                    />
                  </PlanActionsDropdownWrapper>
                }
                listPositionOverride="bottom"
                dropdownAlign="right"
                variant="filledBlue"
                withoutSeparator
                withoutIcon
                buttonTitle="Выберите действие"
              />
            </>
          ) : (
            <Button
              variant="filled"
              size="regular"
              title={'Показать данные на выбранную дату'}
              onClick={() => {
                if (range) {
                  console.log('Показать данные на выбранную дату:', range[0]);
                  onShowSingle?.(range[0]);
                  onCloseDropdown?.();
                }
              }}
            />
          )}
        </BottomActionBar>
      )}
      <div
        style={{
          position: 'fixed',
          left: tooltipLeft,
          top: tooltipTop,
          width: tooltipWidth,
          pointerEvents: 'none',
          zIndex: 1000,
          display: tooltipVisible ? 'block' : 'none',
        }}>
        <Tooltip isVisible={true} text={tooltipText} displayOnTop={false} />
      </div>
    </SelectDateInputWrapper>
  );
}
